// Structure the publisher gave with layout and we lost with it, put back as the reader's own.
// Runs on the rendered article (the web reader and the phone's reader page, which gets this
// file on its own via packages/tokens/generate.mjs, so it has no imports). It works on what
// any stored copy holds, old or new:
//
//   - lines of "•" bullets under a short title (Pinkbike's spec box)        → a titled box
//   - Specifications / Key facts headings with their list or table          → a titled box
//   - "+" / "−" lines, or Pros/Cons, Tops/Flops headings with their lists   → two columns
//   - a quote laid out as a table (an icon cell and a text cell)           → a pull quote
//   - a Conclusion / Verdict / "Pinkbike's Take" and what follows it         → a verdict box
//   - a post from X, Bluesky, Threads or Mastodon quoted as a block          → a post card
//   - a carousel's slides ("3/10"), or a run of bare photos                  → a photo grid
//   - an ALL-CAPS line standing alone ("PERFORMANCE")                        → a subhead
//   - "Photo: …" at the end of a caption                                    → a quiet credit
//   - "Must Read This Week", deal widgets, other stories, the writer's card,
//     share buttons, comment links, tag lines and newsletter sign-ups      → removed
//   - empty blocks, stray line breaks and doubled rules                      → removed

/* eslint-disable @typescript-eslint/no-explicit-any */
const BULLET = /^\s*([•·+\-–−])\s*/;
const PROS = /^(?:pros|tops|the good|likes|positives|highs|we dig|what we like)\s*:?$/i;
const CONS = /^(?:cons|flops|the bad|dislikes|negatives|lows|we don['’]t(?: dig)?|what we don['’]t like)\s*:?$/i;
const SPECS = /^(?:specifications|specs|tech(?:nical)? (?:specs|data|details)|key (?:facts|features|specs)|at a glance|geometry)\s*:?$/i;
const VERDICT = /^(?:in )?conclusions?$|^(?:(?:our|the|final) )?verdict(?: on .{2,40})?$|^(?:the )?bottom line$|^final (?:thoughts|words)$|^[\w-]+['’]s take$|^our take$/i;
const text = (n: any) => (n?.textContent || "").replace(/\s+/g, " ").trim();
const BLOCK = /^(?:DIV|P|UL|OL|DL|TABLE|FIGURE|BLOCKQUOTE|H[1-6]|SECTION|PRE|IFRAME|VIDEO|HR|BUTTON)$/;
const HEADING = /^H[1-4]$/;
const MEDIA = "img, picture, video, iframe, hr, table, svg, button";

export function enhanceArticle(root: any): void {
  if (!root || root.dataset?.enhanced) return;
  if (root.dataset) root.dataset.enhanced = "1";
  const doc = root.ownerDocument || root;
  dropPageTails(root);
  dropLeftovers(root);
  galleries(root, doc);
  // deepest first, so an inner block is shaped before the one around it is looked at
  for (const el of (Array.from(root.querySelectorAll("div, p, td, section")) as any[]).reverse()) lineList(el, doc);
  headedLists(root);
  pairProsCons(root, doc);
  quoteTables(root, doc);
  postCards(root, doc);
  verdicts(root, doc);
  subheads(root, doc);
  credits(root, doc);
  tables(root, doc);
  speakerTurns(root);
  tidy(root);
}

/**
 * An interview set as bold questions and plain answers (The Verge's Decoder): the questions
 * become speaker turns (p.q), set quieter than the answers rather than shouting in bold.
 * Only when there are at least three, each a real sentence followed by an answer.
 */
function speakerTurns(root: any): void {
  const squash = (s: string) => s.replace(/\s+/g, " ").trim();
  /** A paragraph that is all bold: its outermost bold runs say everything it says. */
  const allBold = (p: any) => {
    const said = (Array.from(p.querySelectorAll("b, strong")) as any[]).filter((b) => !b.parentElement?.closest("b, strong")).map((b) => b.textContent ?? "").join("");
    return squash(said).length >= squash(p.textContent ?? "").length - 2;
  };
  const turns = (Array.from(root.querySelectorAll("p")) as any[]).filter((p) => {
    if (p.closest("blockquote, li, table, figure, .box, .verdict, .post, .pc")) return false;
    if (squash(p.textContent ?? "").length < 20 || !allBold(p)) return false;
    const answer = p.nextElementSibling;
    return answer?.nodeName === "P" && !allBold(answer);
  });
  if (turns.length < 3) return;
  for (const p of turns) p.classList.add("q");
}

const NUMBER = /^[~≈±<>]?[$€£]?\s?[+\-−–]?\d[\d.,:'’]*\s?(?:%|°|x|×|mm|cm|m|km|in|g|kg|lb|lbs|s|ms|h|min|w|wh|mah|psi|bar|nm|fps|hz|gb|tb|mb)?(?:\s?[/–-]\s?[$€£]?\d[\d.,]*\s?\S{0,3})?$/i;

/**
 * Tables that read on a phone. A matrix of short values (a geometry chart: sizes across,
 * measurements down) scrolls sideways under its pinned first column; a list of labelled
 * values (a spec table) reads as label and value; records with a header row (a schedule,
 * test results) become one card per row on a narrow screen, each value under its column's
 * name. A title row spanning the table becomes its caption; numbers line up on the right.
 */
function tables(root: any, doc: any): void {
  for (const t of Array.from(root.querySelectorAll("table")) as any[]) {
    if (t.closest(".tw, .post, figure[data-compare]")) continue;
    const cellsOf = (r: any) => (Array.from(r.children) as any[]).filter((c) => c.nodeName === "TD" || c.nodeName === "TH");
    let rows = (Array.from(t.querySelectorAll("tr")) as any[]).filter((r) => r.closest("table") === t && cellsOf(r).some((c) => text(c)));
    if (rows.length < 2) continue;
    const across = Math.max(...rows.map((r) => cellsOf(r).reduce((n: number, c: any) => n + (Number(c.getAttribute("colspan")) || 1), 0)));
    // a first row that is one cell across the table is its title ("Specifications")
    const first = cellsOf(rows[0]);
    if (first.length === 1 && across > 1 && text(first[0]).length <= 80 && !t.querySelector("caption")) {
      const cap = doc.createElement("caption");
      cap.textContent = text(first[0]);
      t.prepend(cap);
      rows[0].remove();
      rows = rows.slice(1);
    }
    const spans = t.querySelector("[colspan], [rowspan]") && Array.from(t.querySelectorAll("[colspan], [rowspan]")).some((c: any) => (Number(c.getAttribute("colspan")) || 1) > 1 || (Number(c.getAttribute("rowspan")) || 1) > 1);
    // a column empty in every row (a spacer) is no column
    let width = Math.max(...rows.map((r) => cellsOf(r).length));
    if (!spans) {
      for (let col = width - 1; col >= 0; col--) {
        if (rows.some((r) => { const c = cellsOf(r)[col]; return c && (text(c) || c.querySelector("img")); })) continue;
        for (const r of rows) cellsOf(r)[col]?.remove();
        width--;
      }
    }
    const headRow = t.querySelector("thead tr") || (cellsOf(rows[0]).every((c) => c.nodeName === "TH") ? rows[0] : null);
    const head = headRow ? cellsOf(headRow).map((c) => text(c)) : null;
    const body = rows.filter((r) => r !== headRow);
    const data = body.flatMap((r) => cellsOf(r).slice(1)).map((c) => text(c)).filter(Boolean);
    const short = data.length ? data.filter((x) => x.length <= 16).length / data.length : 0;
    const kind = width === 2 && !spans ? "kv"
      : width >= 3 && short >= 0.8 ? "matrix"
      : head && width <= 6 && !spans && body.length ? "cards"
      : "scroll";
    // numbers on the right, in even widths, in the columns that hold them (under a heading that
    // may be a plain cell: "Size · S · M · L")
    for (let col = 1; col < width; col++) {
      const column = rows.map((r) => cellsOf(r)[col]).filter((c) => c && text(c));
      const values = column[0] && !NUMBER.test(text(column[0])) ? column.slice(1) : column;
      if (!spans && values.length && values.every((c) => NUMBER.test(text(c)))) for (const c of column) c.classList.add("num");
    }
    if (kind === "cards") {
      if (headRow && headRow !== t.querySelector("thead tr")) headRow.classList.add("head");
      for (const r of body) cellsOf(r).forEach((c, i) => { if (head![i]) c.setAttribute("data-label", head![i]); });
    }
    const wrap = doc.createElement("div");
    wrap.className = `tw ${kind}${width >= 5 ? " dense" : ""}`;
    t.replaceWith(wrap);
    wrap.append(t);
  }
}

/** A block of "•" / "+" / "−" lines separated by line breaks, with at most a title line first. */
function lineList(el: any, doc: any): void {
  if (!el.isConnected || !el.querySelector(":scope > br") || el.closest(".box")) return;
  const lines: any[][] = [[]];
  for (const n of Array.from(el.childNodes) as any[]) {
    if (n.nodeName === "BR") { lines.push([]); continue; }
    if (n.nodeType === 1 && BLOCK.test(n.nodeName)) { lines.push([n], []); continue; }
    lines[lines.length - 1]!.push(n);
  }
  const lineText = (l: any[]) => text({ textContent: l.map((n) => n.textContent).join("") });
  // a line with no words that still holds something (a photo between the bullets) would be lost: leave the block as it is
  if (lines.some((l) => !lineText(l) && l.some((n) => n.nodeType === 1 && (n.matches?.(MEDIA) || n.querySelector?.(MEDIA))))) return;
  const full = lines.filter((l) => lineText(l));
  if (full.length < 2) return;
  const head = BULLET.test(lineText(full[0]!)) ? null : full[0]!;
  const items = head ? full.slice(1) : full;
  if (items.length < 2 || !items.every((l) => BULLET.test(lineText(l)))) return;
  if (head && lineText(head).length > 80) return;
  // or a title of its own just before it ("Pros" in a paragraph above the lines)
  const above = head ? null : el.previousElementSibling;
  const aboveTitle = above && !above.querySelector("img, figure, iframe") && text(above).length <= 60 && !BULLET.test(text(above)) ? text(above) : "";
  const title = head ? lineText(head) : aboveTitle;
  const marks = items.map((l) => BULLET.exec(lineText(l))![1]);
  const kind = marks.every((m) => m === "+") ? "pros" : marks.every((m) => /[-–−]/.test(m!)) ? "cons" : "list";
  // "-" lines with no title are as likely a dashed list in prose as cons, and two bare "•" lines
  // as likely a quirk: only those with a title, or longer lists, become boxes
  if (!title && (kind === "cons" || items.length < 3)) return;
  if (!head && aboveTitle) above.remove();
  const box = doc.createElement("div");
  box.className = `box ${kind}`;
  if (title) { const h = doc.createElement("h4"); h.textContent = title; box.append(h); }
  const ul = doc.createElement("ul");
  for (const l of items) {
    const li = doc.createElement("li");
    for (const n of l) li.append(n);
    stripMark(li);
    ul.append(li);
  }
  box.append(ul);
  el.replaceChildren(box);
}

/** Removes the bullet character a line starts with (a text node, or an element holding only it). */
function stripMark(li: any): void {
  const walker = li.ownerDocument.createTreeWalker(li, 4 /* text */);
  for (let t = walker.nextNode(); t; t = walker.nextNode()) {
    if (!t.textContent.trim()) continue;
    t.textContent = t.textContent.replace(BULLET, "");
    if (!t.textContent.trim() && t.parentNode !== li && !text(t.parentNode)) t.parentNode.remove();
    return;
  }
}

/** "Pros" / "Tops" / "Specifications" headings (or bold lines) followed by a list: each becomes a box. */
function headedLists(root: any): void {
  for (const h of Array.from(root.querySelectorAll("h2, h3, h4, h5, p, div, strong, b")) as any[]) {
    if (!h.isConnected || h.closest(".box")) continue;
    const t = text(h);
    const kind = PROS.test(t) ? "pros" : CONS.test(t) ? "cons" : SPECS.test(t) ? "list" : null;
    if (!kind) continue;
    let holder = h.nodeName === "STRONG" || h.nodeName === "B" ? h.parentElement : h;
    // the title alone in a wrapper of its own, with the list beside the wrapper (The Loam Wolf)
    while (holder && !holder.nextElementSibling && holder.parentElement && holder.parentElement !== root && text(holder.parentElement) === t) holder = holder.parentElement;
    let list = holder?.nextElementSibling;
    while (list && !text(list) && !/^(?:UL|OL|DL|TABLE)$/.test(list.nodeName)) list = list.nextElementSibling;
    // a specification box may hold a table; pros and cons are always lists
    const fits = list && (/^(?:UL|OL)$/.test(list.nodeName) || (kind === "list" && /^(?:DL|TABLE)$/.test(list.nodeName)));
    if (!holder || !fits || text(holder) !== t) continue;
    // a little thumbs-up/down icon just above it (ENDURO): the box's own markers say the same
    const icon = holder.previousElementSibling;
    if (icon && !text(icon) && icon.querySelectorAll("img").length === 1 && !icon.querySelector("figure, iframe")) icon.remove();
    const box = holder.ownerDocument.createElement("div");
    box.className = `box ${kind}`;
    const title = holder.ownerDocument.createElement("h4");
    title.textContent = t.replace(/:$/, "");
    holder.replaceWith(box);
    box.append(title, list);
  }
}

/** A pros box and the cons box that follows it sit side by side. */
function pairProsCons(root: any, doc: any): void {
  for (const pros of Array.from(root.querySelectorAll(".box.pros")) as any[]) {
    if (pros.closest(".pc")) continue;
    const all = Array.from(root.querySelectorAll(".box")) as any[];
    const cons = all[all.indexOf(pros) + 1];
    if (!cons || !cons.classList.contains("cons")) continue;
    const pc = doc.createElement("div");
    pc.className = "pc";
    pros.replaceWith(pc);
    pc.append(pros, cons);
  }
}

/** A table used to lay out a quote (an icon cell, then the words): a pull quote with its author. */
function quoteTables(root: any, doc: any): void {
  for (const table of Array.from(root.querySelectorAll("table")) as any[]) {
    const cells = Array.from(table.querySelectorAll("td")) as any[];
    if (cells.length !== 2 || text(cells[0]) || !cells[0].querySelector("img") || text(cells[1]).length < 40) continue;
    const words = text(cells[1]);
    const m = /^(.*?)\s*[—–]\s*([^—–]{2,60})$/.exec(words);
    const q = doc.createElement("blockquote");
    q.className = "pull";
    const p = doc.createElement("p");
    p.textContent = m ? m[1]! : words;
    q.append(p);
    if (m) { const c = doc.createElement("cite"); c.textContent = m[2]!.trim(); q.append(c); }
    table.replaceWith(q);
  }
}

const POST = /^https?:\/\/(?:www\.|mobile\.)?(?:(?:twitter|x)\.com\/(\w+)\/status\/\d+|bsky\.app\/profile\/([^/?#]+)\/post\/\w+|threads\.(?:net|com)\/@([\w.]+)\/post\/[\w-]+|[\w-]+\.social\/@(\w+)\/\d+)/i;
const NETWORK = (href: string) => /bsky\.app/.test(href) ? "Bluesky" : /threads\./.test(href) ? "Threads" : /\.social\//.test(href) ? "Mastodon" : "X";

/**
 * A post from a social network, quoted as a block whose script (which would draw the card)
 * the sanitizer removed: its words, who posted it, and a link to it, as a card.
 */
function postCards(root: any, doc: any): void {
  for (const q of Array.from(root.querySelectorAll("blockquote")) as any[]) {
    if (!q.isConnected || q.classList.contains("pull") || q.closest(".post") || text(q).length > 1500) continue;
    // the permalink is the last link to the post (the date at the end)
    const links = (Array.from(q.querySelectorAll("a[href]")) as any[]).filter((a) => POST.test(a.getAttribute("href")));
    const link = links[links.length - 1];
    if (!link) continue;
    const href = link.getAttribute("href");
    const m = POST.exec(href)!;
    const all = text(q);
    const by = /[—–]\s*([^()—–]{1,60}?)\s*\((@[^)\s]{1,60})\)/.exec(all);
    const handle = by?.[2] ?? (m[1] || m[3] || m[4] || (m[2] && !m[2].startsWith("did:") ? m[2] : "") ? `@${m[1] || m[3] || m[4] || m[2]}` : "");
    const words = doc.createElement("div");
    words.className = "post-text";
    for (const p of Array.from(q.children) as any[]) {
      const t = text(p);
      if (p.nodeName !== "P" || !t || /^[—–]/.test(t) || /^\[image or embed\]$/i.test(t)) continue;
      words.append(p);
    }
    // an X post: its words, then "— Name (@handle) date" loose in the block
    if (!words.childNodes.length) { const p = doc.createElement("p"); p.textContent = all.split(/\s[—–]\s/)[0]!; words.append(p); }
    // X's own short links (to the photo or the post itself) say nothing on their own
    for (const a of Array.from(words.querySelectorAll("a")) as any[]) if (/^(?:pic\.twitter\.com\/|https?:\/\/t\.co\/)/.test(text(a))) a.remove();
    const walker = doc.createTreeWalker(words, 4 /* text */);
    for (let t = walker.nextNode(); t; t = walker.nextNode()) t.textContent = t.textContent.replace(/\s*https?:\/\/t\.co\/\w+/g, "");
    for (const br of Array.from(words.querySelectorAll("br")) as any[]) if (!br.nextSibling || !text({ textContent: br.nextSibling.textContent })) br.remove();
    const card = doc.createElement("div");
    card.className = "post";
    const head = doc.createElement("div");
    head.className = "post-h";
    if (by) { const b = doc.createElement("b"); b.textContent = by[1]!.trim(); head.append(b, " "); }
    const who = doc.createElement("span");
    who.textContent = [handle, NETWORK(href)].filter(Boolean).join(" · ");
    head.append(who);
    const go = doc.createElement("a");
    go.className = "post-a";
    go.setAttribute("href", href);
    go.setAttribute("target", "_blank");
    go.setAttribute("rel", "noopener noreferrer");
    go.textContent = `View on ${NETWORK(href)}`;
    card.append(head, words, go);
    q.replaceWith(card);
  }
}

/** The element a line stands alone in: a heading, or a paragraph or block of one short line. */
function lineOf(el: any): string {
  if (el.querySelector(MEDIA) || el.querySelector("p, div, ul, ol, br, blockquote")) return "";
  const t = text(el);
  return t.length <= 60 ? t : "";
}

/** The next element after [el] that is not just space or a line break. */
function nextBlock(el: any): any {
  let n = el.nextSibling;
  while (n && (n.nodeName === "BR" || (n.nodeType === 3 && !n.textContent.trim()) || (n.nodeType === 1 && !text(n) && !n.querySelector(MEDIA) && !n.matches(MEDIA)))) n = n.nextSibling;
  return n?.nodeType === 1 ? n : null;
}

/**
 * The review's conclusion (Conclusion, Verdict, "Pinkbike's Take") and what follows it, up to
 * the next heading or rule, as one box; a score with a label ("Rating: 8/10") as a badge.
 */
function verdicts(root: any, doc: any): void {
  for (const h of Array.from(root.querySelectorAll("h2, h3, h4, h5, p, div")) as any[]) {
    if (!h.isConnected || h.closest(".box, .verdict, blockquote, li, .post")) continue;
    const t = lineOf(h).replace(/:$/, "");
    if (!t || !VERDICT.test(t)) continue;
    const parts: any[] = [];
    for (let n = nextBlock(h); n && parts.length < 5; n = nextBlock(n)) {
      if (HEADING.test(n.nodeName) || n.nodeName === "HR" || n.matches(".box, .pc, .gallery") || n.querySelector("h1, h2, h3, h4")) break;
      parts.push(n);
    }
    if (!parts.length) continue;
    const box = doc.createElement("section");
    box.className = "verdict";
    const label = doc.createElement("h4");
    label.textContent = t;
    h.replaceWith(box);
    box.append(label, ...parts);
  }
  const SCORE = /^(?:([a-z][\w' ]{1,24}?)\s*:?\s+)?(\d{1,3}(?:\.\d)?)\s*(?:\/|out of)\s*(5|10|100)$/i;
  for (const el of Array.from(root.querySelectorAll("p, h2, h3, h4, h5")) as any[]) {
    const m = SCORE.exec(lineOf(el));
    // a bare "1/5" outside a verdict is as likely a slide counter as a score
    if (!m || (!m[1] && !el.closest(".verdict")) || Number(m[2]) > Number(m[3])) continue;
    if (m[1] && !/score|rating|verdict|overall|grade/i.test(m[1])) continue;
    const p = doc.createElement("p");
    p.className = "score";
    const b = doc.createElement("b"); b.textContent = m[2]!;
    const of = doc.createElement("span"); of.textContent = `/${m[3]}`;
    p.append(b, of);
    if (m[1]) { const s = doc.createElement("small"); s.textContent = m[1].trim(); p.append(s); }
    el.replaceWith(p);
  }
}

/**
 * Subheads written as a line of capitals ("PERFORMANCE"), or as a short line just above a box
 * or pull quote, become headings. The capitals stay: they are how the writer set it.
 */
function subheads(root: any, doc: any): void {
  for (const el of Array.from(root.querySelectorAll("p, div")) as any[]) {
    if (!el.isConnected || el.closest(".box, .verdict, li, table, blockquote, figure, .post, .gallery")) continue;
    const t = lineOf(el);
    if (!t || t.length < 3 || /[.!?,;]$/.test(t) || el.querySelector("a")) continue;
    const caps = /[A-Z]{3}/.test(t) && !/[a-z]/.test(t) && t.split(/\s+/).length <= 6;
    const next = nextBlock(el);
    const titlesBox = t.length <= 40 && !!next?.matches?.(".box, .pc, blockquote.pull");
    if (!caps && !titlesBox) continue;
    const h = doc.createElement("h3");
    if (caps) h.className = "caps";
    h.textContent = t.replace(/:$/, "");
    el.replaceWith(h);
  }
}

const CREDIT = /(?:^|[\s(|·–—-])((?:photos?|images?|credits?|photograph(?:y|s)?|illustrations?|graphics?|source)(?:\s+(?:by|courtesy(?: of)?))?\s*:\s*[^:]{2,90}?|(?:photos?|images?|illustrations?) (?:by|courtesy(?: of)?) [A-Z][^]{1,80}?)\)?\s*$/i;

/**
 * "Photo: Name / Publisher" at the end of a caption is set apart, smaller and quieter, so the
 * caption reads as a caption; a credit on its own line just below a photo joins its caption.
 */
function credits(root: any, doc: any): void {
  for (const p of Array.from(root.querySelectorAll("p")) as any[]) {
    const t = text(p);
    const fig = p.previousElementSibling;
    if (t.length > 100 || fig?.nodeName !== "FIGURE" || !/^(?:photos?|images?|credits?)(?: by)?\s*:/i.test(t) || p.querySelector(MEDIA)) continue;
    let cap = fig.querySelector("figcaption");
    if (!cap) { cap = doc.createElement("figcaption"); fig.append(cap); }
    const s = doc.createElement("small");
    s.className = "credit";
    s.textContent = t;
    cap.append(s);
    p.remove();
  }
  for (const cap of Array.from(root.querySelectorAll("figcaption")) as any[]) {
    if (cap.querySelector(".credit")) continue;
    const raw = cap.textContent || "";
    const m = CREDIT.exec(raw.replace(/\s+$/, ""));
    if (!m) continue;
    const at = raw.lastIndexOf(m[1]!);
    if (!raw.slice(0, at).replace(/[\s(|·–—-]+/g, "")) { cap.classList.add("credit-only"); continue; }
    // split the caption where the credit starts; everything from there moves into the credit
    const walker = doc.createTreeWalker(cap, 4 /* text */);
    let seen = 0, start: any = null;
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      const len = n.textContent.length;
      if (seen + len > at) {
        start = n;
        if (at > seen) {
          start = doc.createTextNode(n.textContent.slice(at - seen));
          n.textContent = n.textContent.slice(0, at - seen);
          n.parentNode.insertBefore(start, n.nextSibling);
        }
        break;
      }
      seen += len;
    }
    if (!start) continue;
    let top = start;
    while (top.parentNode !== cap) {
      // the credit starts inside an element (<em>Photo: …</em>): that element must hold only the credit
      if (top.previousSibling && text(top.previousSibling)) { top = null; break; }
      top = top.parentNode;
    }
    if (!top) continue;
    const s = doc.createElement("small");
    s.className = "credit";
    for (let n = top; n; ) { const next = n.nextSibling; s.append(n); n = next; }
    cap.append(s);
    // the separator the caption had before its credit ("… coffee. (", " | ")
    const before = s.previousSibling;
    if (before?.nodeType === 3) before.textContent = before.textContent.replace(/[\s(|·–—-]+$/, " ");
    if (/\)$/.test(text(s)) && !/\(/.test(text(s))) s.lastChild.textContent = s.lastChild.textContent.replace(/\)\s*$/, "");
  }
}

const COUNTER = /^\d{1,3}\s*\/\s*\d{1,3}$/;

/** The one photo a block shows, when it shows nothing else (its text is the photo's caption). */
function photoIn(el: any): any {
  if (el.nodeName === "FIGURE") return el.querySelectorAll("img").length === 1 && !el.querySelector("iframe, video") && !el.hasAttribute("data-compare") ? el : null;
  if (el.nodeName === "IMG") return el;
  const imgs = el.querySelectorAll("img");
  if (imgs.length !== 1 || el.querySelector("iframe, video, table, ul, ol")) return null;
  const fig = el.querySelector("figure");
  if (fig) return text(el) === text(fig) ? photoIn(fig) : null;
  return text(el) ? null : imgs[0];
}

/**
 * A carousel's slides (The Verge's galleries: a photo and its "3/10" per slide), or a run of
 * four or more bare photos, become a grid of photos; a tap opens them full screen (media.ts).
 */
function galleries(root: any, doc: any): void {
  // the counters say the photos were slides; they go, and their slides become plain figures
  for (const c of Array.from(root.querySelectorAll("h2, h3, h4, h5, h6, p, span, div")) as any[]) {
    if (!c.isConnected || !COUNTER.test(text(c)) || c.querySelector("img")) continue;
    const slide = c.parentElement;
    c.remove();
    const photo = slide && slide !== root ? photoIn(slide) : null;
    if (!photo) continue;
    let fig = photo.nodeName === "FIGURE" ? photo : null;
    if (!fig) { fig = doc.createElement("figure"); photo.replaceWith(fig); fig.append(photo); }
    fig.setAttribute("data-slide", "");
    if (slide !== fig && slide.parentElement) slide.replaceWith(fig);
  }
  const done = new Set<any>();
  for (const first of Array.from(root.querySelectorAll("figure, img")) as any[]) {
    if (done.has(first) || !first.isConnected || first.closest(".gallery, .cmp, .box, a")) continue;
    if (first.nodeName === "IMG" && (first.closest("figure") || first.classList.contains("tiny"))) continue;
    // the run of photo-only siblings this one starts
    const run: any[] = [];
    for (let n: any = first; n; n = nextBlock(n)) {
      const p = photoIn(n);
      if (!p) break;
      run.push(n);
      done.add(n);
    }
    const slides = run.filter((n) => n.hasAttribute?.("data-slide")).length;
    const bare = run.every((n) => !text(n));
    if (!(slides >= 2 || (run.length >= 4 && bare))) continue;
    const grid = doc.createElement("div");
    grid.className = "gallery";
    run[0].replaceWith(grid);
    for (const n of run) {
      const p = photoIn(n);
      let fig = p.nodeName === "FIGURE" ? p : null;
      if (!fig) { fig = doc.createElement("figure"); fig.append(p); }
      fig.removeAttribute("data-row");
      // a tile is a third or half of the column, not the whole of it (the first spans it)
      const img = fig.querySelector("img");
      if (img?.hasAttribute("srcset") && grid.childElementCount) img.setAttribute("sizes", "(max-width: 560px) 50vw, 340px");
      grid.append(fig);
      if (n !== fig && n.isConnected) n.remove();
    }
    const note = doc.createElement("p");
    note.className = "gallery-n";
    note.textContent = `${run.length} photos`;
    grid.after(note);
  }
}

/** What a page appends after the article: its "most read" list and deal widgets. */
function dropPageTails(root: any): void {
  // innermost first: in a short post the whole article is under 400 characters too, and only
  // the deal widget itself may go
  for (const el of (Array.from(root.querySelectorAll("div, p, h2, h3, h4, span")) as any[]).reverse()) {
    if (!el.isConnected) continue;
    const t = text(el);
    // the site's own lists after the article, and its "about us" box: from there to the end
    if (/^must read this week$/i.test(t) || (/^about the (?:author|writer|wolf)$/i.test(t) && textAfter(el, root).length < 1500)) {
      cutFrom(el.nodeName === "SPAN" ? el.parentElement : el, root);
    } else if (t.length < 400 && /\baffiliate commission\b/i.test(t) && /\bdeals?\b/i.test(t)) {
      el.remove();
    }
  }
  dropTeaserLists(root);
}

/** Removes [el] and everything after it, to the end of the article. */
function cutFrom(el: any, root: any): void {
  if (!el) return;
  for (let n: any = el; n && n !== root; n = n.parentElement) while (n.nextSibling) n.nextSibling.remove();
  el.remove();
}

/** The text that follows [el] up to the end of the article. */
function textAfter(el: any, root: any): string {
  let after = "";
  for (let n: any = el; n && n !== root; n = n.parentElement) for (let s = n.nextSibling; s; s = s.nextSibling) after += s.textContent || "";
  return after.replace(/\s+/g, " ").trim();
}

/**
 * A list of other stories at the end (Boing Boing's next posts): two or more items, each a
 * linked headline with an image or a "Read the rest" link. Only at the end, so a list of
 * links the writer made in the article stays.
 */
function dropTeaserLists(root: any): void {
  const more = /^(read (the rest|more)|continue reading|keep reading)\b/i;
  for (const list of Array.from(root.querySelectorAll("ul, ol")) as any[]) {
    if (!list.isConnected) continue;
    const items = Array.from(list.children) as any[];
    if (items.length < 2) continue;
    const teaser = (li: any) => {
      const h = li.querySelector("h2, h3, h4");
      const link = h?.querySelector("a[href]");
      if (!link || text(link) !== text(h)) return false;
      return !!li.querySelector("img") || (Array.from(li.querySelectorAll("a")) as any[]).some((a) => more.test(text(a)));
    };
    if (!items.every(teaser)) continue;
    // what follows the list, up to the end of the article, is no more than a line or two
    if (textAfter(list, root).length > 200) continue;
    // the list's own wrapper goes with it when it holds nothing else
    let gone = list;
    while (gone.parentElement && gone.parentElement !== root && text(gone.parentElement) === text(gone)) gone = gone.parentElement;
    gone.remove();
  }
}

const SHARE = /^(?:(?:share|tweet|follow|email|print|copy link|facebook|twitter|reddit|pin it|linkedin|whatsapp)(?: (?:this|on \w+))?[\s|·•,]*){1,8}$/i;
const COMMENTS = /^(?:comment on substack|leave a comment|view comments|join the discussion|\d+ comments?|comments?)$/i;
const NEWSLETTER = /^(?:-+>\s*)?(?:sign up|subscribe|get (?:our|the)|join (?:our|the))\b.{0,140}?\bnewsletters?\b/i;

/**
 * Page furniture that came along with the article: the writer's card ("Author Info"), share
 * and follow buttons, a comment link, "Posted In:" tag lines, a line of only tag links at the
 * end, and a newsletter sign-up line.
 */
function dropLeftovers(root: any): void {
  for (const el of Array.from(root.querySelectorAll("p, div, section, aside, ul, header, footer")) as any[]) {
    if (!el.isConnected || el.querySelector("figure, iframe, video, table") || (el.querySelector("img") && !/^(?:author info|about the author)/i.test(text(el)))) continue;
    const t = text(el);
    if (!t) continue;
    const gone =
      (/^(?:author info|about the author)\b/i.test(t) && t.length < 900) ||
      (t.length < 120 && SHARE.test(t)) ||
      /^share (?:this|the) [\w ]{2,30}$/i.test(t) ||
      COMMENTS.test(t) ||
      (/^(?:posted in|filed under|tags?|categories|topics)\s*:/i.test(t) && t.length < 300) ||
      (t.length < 220 && NEWSLETTER.test(t)) ||
      tagLine(el, t, root) ||
      (DATE_LINE.test(t) && !textBefore(el, root));
    if (gone) el.remove();
  }
}

/** A date alone on a line ("Oct 15, 2026", "15 October 2026"): the page's dateline, which the reader's header shows already. */
const DATE_LINE = /^(?:(?:mon|tue|wed|thu|fri|sat|sun)[a-z]*,?\s+)?(?:[a-z]{3,9}\.?\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4}|\d{1,2}(?:st|nd|rd|th)?\s+[a-z]{3,9}\.?,?\s+\d{4})$/i;

/** The text that comes before [el] in the article. */
function textBefore(el: any, root: any): string {
  let before = "";
  for (let n: any = el; n && n !== root; n = n.parentElement) for (let s = n.previousSibling; s; s = s.previousSibling) before += s.textContent || "";
  return before.replace(/\s+/g, " ").trim();
}

/** A line of nothing but two or more short links at the very end ("CRIME / SCAMS"). */
function tagLine(el: any, t: string, root: any): boolean {
  if (t.length > 80) return false;
  const links = Array.from(el.querySelectorAll("a")) as any[];
  // links to the site's own tag pages, not a row of "listen on" links under a podcast
  if (links.length < 2 || links.some((a) => text(a).length > 30 || !/\/(?:tags?|category|categories|topics?|section)\//i.test(a.getAttribute("href") || ""))) return false;
  let rest = t;
  for (const a of links) rest = rest.replace(text(a), "");
  return !/[^\s/|,•·–-]/.test(rest) && !textAfter(el, root);
}

/** Empty blocks, line breaks that only make gaps, and rules with nothing to separate. */
function tidy(root: any): void {
  for (const el of (Array.from(root.querySelectorAll("p, div, section, span, figure, header, footer, blockquote, h1, h2, h3, h4, h5, h6, li, ul, ol")) as any[]).reverse()) {
    if (el.isConnected && !text(el) && !el.querySelector(MEDIA)) el.remove();
  }
  const near = (n: any, way: "previousSibling" | "nextSibling") => {
    let s = n[way];
    while (s && s.nodeType === 3 && !s.textContent.trim()) s = s[way];
    return s;
  };
  const block = (n: any) => n?.nodeType === 1 && (BLOCK.test(n.nodeName) || n.nodeName === "FIGURE");
  for (const br of Array.from(root.querySelectorAll("br")) as any[]) {
    const prev = near(br, "previousSibling"), next = near(br, "nextSibling");
    // at the edge of a block, beside one, or the third in a row
    if (!prev || !next || block(prev) || block(next) || (prev.nodeName === "BR" && near(prev, "previousSibling")?.nodeName === "BR")) br.remove();
  }
  for (const hr of Array.from(root.querySelectorAll("hr")) as any[]) {
    const prev = near(hr, "previousSibling"), next = near(hr, "nextSibling");
    // a rule needs something on both sides; a box or heading already sets itself apart
    if (!prev || !next || prev.nodeName === "HR" || HEADING.test(next.nodeName) || next.matches?.(".box, .pc, .verdict, .gallery")) hr.remove();
  }
}
